import { useLazyQuery, useQuery } from '@apollo/client';
import { Button, Container, Divider, Dropdown, FlexGrid, Header, Link, Loader, Message, Text } from '@gasbuddy/react-components';
import { Pagination } from '@gasbuddy/react-consumer-components';
import classnames from 'classnames/bind';
import gql from 'graphql-tag';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import useProfile from '../../../lib/hooks/useProfile';
import { numberFormatter } from '../../../lib/utils';
import { AD_SLOTS } from '../../constants';
import { Ad } from '../Ad';
import Page from '../Page';
import styles from './LeaderboardsPage.module.css';

const cx = classnames.bind(styles);

const MAX_ENTRIES_PER_PAGE = 100;
const MAX_PAGES = 20;

const BANNER_ADS = [
  {
    id: AD_SLOTS[24].slotID,
    path: AD_SLOTS[24].slotName,
    sizes: AD_SLOTS[24].sizes,
    sizeMapping: AD_SLOTS[24].sizeMapping,
  },
  {
    id: AD_SLOTS[23].slotID,
    path: AD_SLOTS[23].slotName,
    sizes: AD_SLOTS[23].sizes,
    sizeMapping: AD_SLOTS[23].sizeMapping,
  },
];

const LEADERBOARDS = {
  ALL_TIME: {
    key: 'allTime',
    label: 'All-Time',
  },
  THIRTY_DAY: {
    key: 'thirtyDay',
    label: 'Thirty Day',
  },
  FIRST_HALF: {
    key: 'firstHalf',
    label: 'First Half',
  },
  SECOND_HALF: {
    key: 'secondHalf',
    label: 'Second Half',
  },
  CONSECUTIVE_DAYS: {
    key: 'consecutiveDays',
    label: 'Consecutive Days',
  },
};

export const GET_MEMBER_RANKS = gql`
  query GetMemberRanks($memberId: ID!) {
    ranks(memberId: $memberId) {
      credits
      leaderboardName
      rank
    }
  }
`;

export const GET_LEADERBOARD = gql`
  query GetLeaderboard($name: String!, $rangeStart: Int, $numEntries: Int) {
    leaderboard(name: $name, rangeStart: $rangeStart, numEntries: $numEntries) {
      credits
      leaderboardName
      memberId
      rank
    }
  }
`;

export default function LeaderboardsPage() {
  const [activeLeaderboard, setActiveLeaderboard] = useState(LEADERBOARDS.ALL_TIME.label);
  const [activePage, setActivePage] = useState(1);
  const { t } = useTranslation('leaderboard');

  const { memberName, isLoggedIn } = useProfile();

  const leaderboardOptions = useMemo(() => {
    // Half Year leaderboards do not rollover until Jan 2nd and July 2nd (and do not provide their year in the name)
    const today = new Date();
    const yesterday = new Date(today.getTime() - 1 * 24 * 60 * 60 * 1000);
    const firstHalfYear = yesterday.getFullYear();
    const secondHalfYear = yesterday.getMonth() < 6 ? firstHalfYear - 1 : firstHalfYear;

    return [
      {
        id: LEADERBOARDS.ALL_TIME.key,
        label: t(LEADERBOARDS.ALL_TIME.key, { ns: 'leaderboard' }),
        value: LEADERBOARDS.ALL_TIME.label,
      },
      {
        id: LEADERBOARDS.THIRTY_DAY.key,
        label: t(LEADERBOARDS.THIRTY_DAY.key, { ns: 'leaderboard' }),
        value: LEADERBOARDS.THIRTY_DAY.label,
      },
      {
        id: LEADERBOARDS.FIRST_HALF.key,
        label: t(LEADERBOARDS.FIRST_HALF.key, { ns: 'leaderboard', year: firstHalfYear }),
        value: LEADERBOARDS.FIRST_HALF.label,
      },
      {
        id: LEADERBOARDS.SECOND_HALF.key,
        label: t(LEADERBOARDS.SECOND_HALF.key, { ns: 'leaderboard', year: secondHalfYear }),
        value: LEADERBOARDS.SECOND_HALF.label,
      },
      {
        id: LEADERBOARDS.CONSECUTIVE_DAYS.key,
        label: t(LEADERBOARDS.CONSECUTIVE_DAYS.key, { ns: 'leaderboard' }),
        value: LEADERBOARDS.CONSECUTIVE_DAYS.label,
      },
    ];
  }, [t]);

  const {
    loading: isFetchingMemberRanks,
    error: memberRanksError,
    data: memberRanksData,
  } = useQuery(GET_MEMBER_RANKS, {
    skip: !isLoggedIn,
    variables: { memberId: memberName },
  });

  const memberRanks = memberRanksData?.ranks || [];
  const activeMemberRank = useMemo(() => {
    if (isLoggedIn) {
      return memberRanks.find(rank => rank.leaderboardName === activeLeaderboard) || {};
    }
    return {};
  }, [activeLeaderboard, isLoggedIn, memberRanks]);

  const memberRankPageNumber = useMemo(() => {
    if (activeMemberRank) {
      // Convert the user's rank to 0-based index (ex: rank of 1200 is on page 12, not 13)
      return Math.floor((activeMemberRank.rank - 1) / MAX_ENTRIES_PER_PAGE) + 1;
    }
    return undefined;
  }, [activeMemberRank]);

  const [fetchLeaderboard, {
    loading: isFetchingLeaderboard,
    error: leaderboardError,
    data: leaderboardData,
  }] = useLazyQuery(GET_LEADERBOARD);

  useEffect(() => {
    fetchLeaderboard({
      variables: {
        name: activeLeaderboard,
        rangeStart: 1,
        numEntries: MAX_ENTRIES_PER_PAGE * MAX_PAGES,
        // ^^ fetch all entries so we can paginate client-side
      },
    });
  }, [activeLeaderboard, fetchLeaderboard]);

  const leaderboardEntries = leaderboardData?.leaderboard || [];
  const activeEntries = useMemo(() => leaderboardEntries.slice(
    // ex: page 2 ranks of 101-200 convert to indices 100-199
    (activePage - 1) * MAX_ENTRIES_PER_PAGE,
    activePage * MAX_ENTRIES_PER_PAGE,
  ), [activePage, leaderboardEntries]);

  const handleLeaderboardChange = useCallback(({ target }) => {
    if (target.value !== activeLeaderboard) {
      setActiveLeaderboard(target.value);
      setActivePage(1);
    }
  }, [activeLeaderboard]);

  const handlePageChange = useCallback((newPage) => {
    if (newPage !== activePage) {
      setActivePage(newPage || 1);
    }
  }, [activePage]);

  const handleJumpToUserPage = useCallback(() => {
    handlePageChange(memberRankPageNumber);
  }, [handlePageChange, memberRankPageNumber]);

  let rankMessage;
  if (!isLoggedIn) {
    rankMessage = (
      <Button
        primary
        name="Log In Button"
        as="a"
        href="/login"
      >
        {t('logInToSeeRank', { ns: 'leaderboard' })}
      </Button>
    );
  } else if (isFetchingMemberRanks) {
    rankMessage = (
      <Loader size="md">
        {t('loadingRanks', { ns: 'leaderboard' })}
      </Loader>
    );
  } else if (memberRanksError) {
    rankMessage = (
      <Message color="red">
        {t('unableToRetrieveRanks', { ns: 'leaderboard' })}
      </Message>
    );
  } else if (!activeMemberRank.rank) {
    rankMessage = (
      <Message color="grey">
        {t('notOnLeaderboard', { ns: 'leaderboard' })}
      </Message>
    );
  } else {
    rankMessage = (
      <Text size="lg" data-testid="member-rank-info">
        {activeLeaderboard === LEADERBOARDS.CONSECUTIVE_DAYS.label
          ? (
            <Trans
              i18nKey="leaderboard:rankDaysMessage"
              rank={activeMemberRank.rank}
              daysAmount={numberFormatter(activeMemberRank.credits.toString())}
            >
              My Rank: <Text bold color="gold">{{ rank: activeMemberRank.rank }}</Text>
              &nbsp;&nbsp;({{ daysAmount: numberFormatter(activeMemberRank.credits.toString()) }}
              &nbsp;days
            </Trans>
          )
          : (
            <Trans
              i18nKey="leaderboard:rankCreditsMessage"
              rank={activeMemberRank.rank}
              creditsAmount={numberFormatter(activeMemberRank.credits.toString())}
            >
              My Rank: <Text bold color="gold">{{ rank: activeMemberRank.rank }}</Text>
              &nbsp;&nbsp;({{ creditsAmount: numberFormatter(activeMemberRank.credits.toString()) }}
              &nbsp;credits
            </Trans>
          )
        }
        {activeMemberRank.rank <= (MAX_ENTRIES_PER_PAGE * MAX_PAGES) && (
          <Fragment>
            ,&nbsp;
            <Button
              className={cx('jump-button')}
              data-testid="jump-to-user-page"
              onClick={handleJumpToUserPage}
              tertiary
            >
              {t('page', { ns: 'leaderboard', pageNumber: memberRankPageNumber })}
            </Button>
          </Fragment>
        )}
        )
      </Text>
    );
  }

  let isLoadingOrErrorMessage;

  if (!isFetchingLeaderboard && leaderboardError) {
    isLoadingOrErrorMessage = (
      <Message color="red">
        {t('unableToRetrieveLeaderboard', { ns: 'leaderboard' })}
      </Message>
    );
  } else if (!isFetchingLeaderboard && leaderboardEntries.length <= 0) {
    isLoadingOrErrorMessage = (
      <Message color="grey">
        {t('noEntriesFound', { ns: 'leaderboard' })}
      </Message>
    );
  } else if (isFetchingLeaderboard) {
    isLoadingOrErrorMessage = (
      <Loader size="md">
        {t('loadingLeaderboard', { ns: 'leaderboard' })}
      </Loader>
    );
  }

  return (
    <Page
      withTopAd
      bannerAds={BANNER_ADS}
      name="Leaderboards"
    >
      <Container className={cx('leaderboards-container')}>
        <Header as="h1">
          {t('header', { ns: 'leaderboard' })}
        </Header>
        <FlexGrid className={cx('leaderboards-wrapper')}>
          <FlexGrid.Column desktop={8}>
            <p>
              <Trans i18nKey="leaderboard:seeHowYouRank">
                See how you rank against our top GasBuddy members when it comes to earning credits.
                <br />
                Don&apos;t see yourself in the leaderboards? Reporting prices can earn you up to 1000 credits per day.
                <br />
                It&apos;s a sure-fire way to improve your rank!
              </Trans>
            </p>
            <div className={cx('leaderboard-area')}>
              <Dropdown
                className={cx('leaderboards-select')}
                id="leaderboardSelect"
                label={t('chooseLeaderboard', { ns: 'leaderboard' })}
                name="leaderboard-select"
                onChange={handleLeaderboardChange}
                options={leaderboardOptions}
              />
              <Divider />
              <Header as="h2">
                {t(
                  `${leaderboardOptions.find(board => board.value === activeLeaderboard).id}Leaders`,
                  { ns: 'leaderboard' },
                )}
              </Header>
              {rankMessage}
              <br /><br />
              {!!isLoadingOrErrorMessage && (
                <Fragment>
                  {isLoadingOrErrorMessage}
                </Fragment>
              )}
              {!isFetchingLeaderboard && !leaderboardError && activeEntries.length > 0 && (
                <Fragment>
                  <Pagination
                    activePage={activePage}
                    onPageChange={handlePageChange}
                    totalPages={Math.ceil(leaderboardEntries.length / MAX_ENTRIES_PER_PAGE)}
                  />
                  <br />
                  <FlexGrid className={cx('leaderboard')}>
                    {activeEntries.map(entry => (
                      <FlexGrid.Column tablet={6} className={cx('leader-cell')} key={`${entry.memberId}`}>
                        <FlexGrid className={cx('leader-wrapper')} data-testid="leaderboard-entry">
                          <FlexGrid.Column mobile={2} className={cx('member-rank')}>
                            <Text bold color={entry.rank === activeMemberRank.rank ? 'gold' : 'midnight'}>
                              {entry.rank}
                            </Text>
                          </FlexGrid.Column>
                          <FlexGrid.Column mobile={7} className={cx('member-name-wrapper')}>
                            <Link to={`/member/${entry.memberId.toLowerCase()}`}>
                              {entry.memberId}
                            </Link>
                          </FlexGrid.Column>
                          <FlexGrid.Column mobile={3} className={cx('member-credits')}>
                            <Text>{numberFormatter(entry.credits.toString())}</Text>
                          </FlexGrid.Column>
                        </FlexGrid>
                      </FlexGrid.Column>
                    ))}
                  </FlexGrid>
                </Fragment>
              )}
            </div>
            <div className={cx('about-leaderboards')}>
              <Header as="h2">
                {t('aboutLeaderboards', { ns: 'leaderboard' })}
              </Header>
              <Text as="p">
                {t('aboutLeaderboardsDescription', { ns: 'leaderboard' })}
              </Text>
              <Header as="h3">
                {t('allTime', { ns: 'leaderboard' })}
              </Header>
              <Text as="p">
                {t('allTimeDescription', { ns: 'leaderboard' })}
              </Text>
              <Header as="h3">
                {t('thirtyDay', { ns: 'leaderboard' })}
              </Header>
              <Text as="p">
                {t('thirtyDayDescription', { ns: 'leaderboard' })}
              </Text>
              <Header as="h3">
                {t('halfYear', { ns: 'leaderboard' })}
              </Header>
              <Text as="p">
                {t('halfYearDescription', { ns: 'leaderboard' })}
              </Text>
              <Header as="h3">
                {t('consecutiveDays', { ns: 'leaderboard' })}
              </Header>
              <Text as="p">
                {t('consecutiveDaysDescription', { ns: 'leaderboard' })}
              </Text>
            </div>
          </FlexGrid.Column>
          <FlexGrid.Column desktop={4} className={cx('ads-container')}>
            {[AD_SLOTS[20], AD_SLOTS[21], AD_SLOTS[22]].map(({ slotID, slotName, sizes, sizeMapping }) => (
              <Fragment key={slotID}>
                <Ad
                  id={slotID}
                  path={slotName}
                  sizes={sizes}
                  sizeMapping={sizeMapping}
                  collapseIfEmpty
                />
                <br />
              </Fragment>
            ))}
          </FlexGrid.Column>
        </FlexGrid>
      </Container>
    </Page>
  );
}
